import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import {
  createPlaybackFrameClock,
  createTimelineStore,
  type PlaybackFrameClock,
  type PlaybackFrameClockStore,
  type PlaybackTransport,
  type TimelineStore,
} from "./playbackTransport";
import { deriveStatus, seekVideo, subscribeVideoEvents } from "./videoEvents";
import type { VideoChannelEndBehavior, VideoChannelSource, VideoTimelineState } from "./types";
import {
  advanceVirtualClock,
  boundToTimeline,
  clamp,
  computePlayStart,
  computeTimelineDuration,
  isChannelPastEnd,
  readFiniteDuration,
  reconcileDurationPatch,
  resolveChannelFrame,
} from "./syncedTimelineMath";
import { VideoElementAttachmentController } from "./videoElementAttachmentController";
import { isMediaReadyToPlay, isMediaStarved, isMediaUnavailable } from "./mediaReadiness";

const DEFAULT_DRIFT_TOLERANCE_SECONDS = 0.18;
const DEFAULT_END_EPSILON_SECONDS = 0.04;
const DEFAULT_BUFFER_AHEAD_SECONDS = 2;
// 버퍼 대기 중 채널을 대기 위치에 맞출 때 이미 그 자리인 채널은 seek하지 않는다. 같은 위치로
// 다시 seek하면 받던 요청이 끊기기 때문이다.
const HOLD_ALIGN_TOLERANCE_SECONDS = 0.001;
const NO_CHANNELS: ReadonlySet<string> = new Set();

function sameChannelSet(a: ReadonlySet<string>, b: ReadonlySet<string>) {
  if (a.size !== b.size) return false;
  for (const channelId of a) if (!b.has(channelId)) return false;
  return true;
}

const DEFAULT_SNAPSHOT: VideoTimelineState = {
  playing: false,
  currentTimeSeconds: 0,
  durationSeconds: 0,
  playbackRate: 1,
  status: "idle",
};

function readVideoDuration(video: HTMLVideoElement) {
  return readFiniteDuration(video.duration);
}

function pauseVideo(video: HTMLVideoElement) {
  try {
    video.pause();
  } catch {
    // 브라우저·테스트 DOM 간 미디어 구현 차이는 무시한다.
  }
}

function releaseVideoResource(video: HTMLVideoElement) {
  pauseVideo(video);
  try {
    video.removeAttribute("src");
    video.load();
  } catch {
    // Media teardown can differ across browsers and test DOMs.
  }
}

function playVideo(video: HTMLVideoElement) {
  try {
    void video.play().catch(() => undefined);
  } catch {
    // 동기적으로 던지는 play() 실패는 무시한다.
  }
}

export interface UseSyncedVideoTimelineOptions {
  channels: readonly VideoChannelSource[];
  /**
   * 초기 텔레메트리(telemetry) 값. 마운트 시 **한 번만** 읽고 이후 변화는 무시한다 —
   * 타임라인은 반환된 명령(command)으로 구동한다. `playing`은 의도적으로 받지 않는다:
   * 재생 시작은 `play()`로 한다(어차피 브라우저가 음소거 아닌 autoplay엔 사용자 제스처를
   * 요구하고, 길이도 먼저 알아야 한다).
   */
  initialState?: Pick<Partial<VideoTimelineState>, "currentTimeSeconds" | "playbackRate">;
  /** 외부 길이 override. 채널 메타데이터보다 우선한다. */
  durationOverrideSeconds?: number | null;
  driftToleranceSeconds?: number;
  endEpsilonSeconds?: number;
  /**
   * 재생 중 텔레메트리(telemetry) emit을 이 ms당 최대 1회로 스로틀(throttle)한다.
   * `undefined`/`<= 0`이면 매 애니메이션 프레임마다 emit한다. 실제 `<video>` 재생은 이와
   * 무관하게 프레임 단위로 매끄럽고, React가 보는 스냅샷(snapshot)만 스로틀된다.
   */
  emitIntervalMs?: number;
  channelEndBehavior?: VideoChannelEndBehavior;
  onTimelineEnded?: (state: VideoTimelineState) => void;
  /**
   * 버퍼 대기 사용 여부. 켜면 다음 프레임을 표시할 수 없는 채널이 있을 때 공통 재생 시간과 모든 영상을 멈춘다.
   * 각 대상 채널이 다음 프레임을 표시할 수 있고 재생 위치 이동(seek)을 끝내야 한다.
   * 각 채널이 앞으로 재생할 데이터를 목표 시간만큼 받았거나 다운로드를 멈춘 상태(NETWORK_IDLE)이면 함께 재생한다.
   * 대기 중에는 `status:"buffering"`을 알린다. 가장 늦은 채널의 위치에서 기다리되,
   * 공통 재생 시간을 되돌리는 폭은 `driftToleranceSeconds`를 넘지 않는다.
   * 재생 시작과 재생 중 위치 이동에도 같은 조건을 적용한다.
   * 끄면(기본값) 공통 재생 시간을 계속 진행하고, 영상과 공통 재생 시간의 차이만 보정한다.
   */
  waitForBuffer?: boolean;
  /**
   * 재생 시작 전에 미리 받을 영상의 길이(초). 기본값은 2다. 남은 영상이 더 짧으면 끝까지 받는다.
   * 다음 프레임을 표시할 수 있고 위치 이동을 끝냈으며 NETWORK_IDLE 상태이면 목표보다 적게 받아도 재생한다.
   * 0이면 모든 대상 채널이 다음 프레임을 표시할 수 있고 위치 이동을 끝낸 순간 재생한다.
   * 음수·유한하지 않은 값은 기본값을 쓴다.
   */
  bufferAheadSeconds?: number;
}

export interface SyncedVideoTimelineApi extends PlaybackTransport {
  /**
   * Creates one identity-scoped video attachment (the returned ref callback). Prefer memoizing it
   * for the lifetime of that rendered video. The legacy wrapper form
   * `(node) => registerVideo(id)(node)` remains cleanup-safe for backward compatibility.
   */
  registerVideo: (channelId: string) => (element: HTMLVideoElement | null) => void;
  getChannelDuration: (channelId: string) => number | null;
  isChannelOutOfTime: (channelId: string) => boolean;
  /** The virtual clock's frame-rate time; `emitIntervalMs` throttles only the snapshot. */
  frameClock: PlaybackFrameClock;
  /**
   * 버퍼 대기 중 이 채널이 아직 출발 조건을 못 채웠는가(늦은 카메라 표시용). 대기가 아니거나
   * `waitForBuffer`가 꺼져 있으면 false. 직접 구현한 timeline은 생략할 수 있다.
   */
  isChannelBuffering?: (channelId: string) => boolean;
}

/**
 * 멀티채널 동기 재생 엔진(headless). virtual clock을 단독으로 소유하고 N개의 `<video>`를
 * 그 시각에 맞춰 끌고 간다. 명령(command)/텔레메트리(telemetry) transport(+ 채널 등록·
 * 채널 길이 조회·blank 판정)를 반환한다.
 * 에피소드가 바뀌면 이 hook을 호출하는 플레이어 컴포넌트의 React key를 바꾼다.
 * React가 이전 컴포넌트를 제거하고 새 컴포넌트를 만들면서 재생 상태·영상·재생 바를 함께 초기화한다.
 * 이 hook은 channels나 URL이 바뀌었다는 이유만으로 에피소드가 바뀌었다고 판단하지 않는다.
 */
export function useSyncedVideoTimeline({
  channels,
  initialState,
  durationOverrideSeconds,
  driftToleranceSeconds = DEFAULT_DRIFT_TOLERANCE_SECONDS,
  endEpsilonSeconds = DEFAULT_END_EPSILON_SECONDS,
  emitIntervalMs,
  channelEndBehavior = "blank",
  onTimelineEnded,
  waitForBuffer = false,
  bufferAheadSeconds = DEFAULT_BUFFER_AHEAD_SECONDS,
}: UseSyncedVideoTimelineOptions): SyncedVideoTimelineApi {
  const [metadataDurations, setMetadataDurations] = useState<Record<string, number>>({});
  const videoAttachmentsRef = useRef<VideoElementAttachmentController | null>(null);
  if (videoAttachmentsRef.current === null) {
    videoAttachmentsRef.current = new VideoElementAttachmentController({
      releaseResource: releaseVideoResource,
    });
  }
  const videoAttachments = videoAttachmentsRef.current;
  const frameRef = useRef<number | null>(null);
  // virtual clock 기준점(anchor): `startedAt`(wall clock) 시점에 위치가 `startedTime`이었다.
  // 이 clock이 타임라인의 유일한 기준이고, 각 video는 그 시각을 따라간다. 외부에서 이 값을
  // 건드리는 경로는 없고, 명령(command)만이 기준점(anchor)을 다시 잡는다.
  const startedAtRef = useRef(0);
  const startedTimeRef = useRef(0);
  // status 집계용 플래그. ended는 가상 클럭이 끝에 닿으면, error는 아무 채널이나 error를 내면 true.
  const endedRef = useRef(false);
  const errorRef = useRef(false);
  // autoplay-on-ready: 길이 로드 전 play()가 불렸을 때 의도를 기록한다. 길이가 들어오는
  // 즉시(정규화 effect) 재개한다. pause/reset이 이 의도를 취소한다.
  const pendingPlayRef = useRef(false);
  // 버퍼 대기: 채널이 다음 프레임을 그릴 수 없어 가상 시계를 세운 위치. null이면 시계가 흐른다.
  // 대기 동안 채널은 모두 이 위치에 멈춰 있고, rAF는 출발 조건만 살핀다.
  const holdSecondsRef = useRef<number | null>(null);
  // 대기 중 아직 출발 조건을 못 채운 채널 — 늦은 카메라 표시가 구독한다.
  const bufferingChannelsRef = useRef<ReadonlySet<string>>(NO_CHANNELS);
  // play()는 아래에서 정의되지만 정규화 effect가 재개 시 호출해야 하므로 ref로 잡는다(TDZ 회피).
  const playRef = useRef<() => void>(() => {});
  const onTimelineEndedRef = useRef(onTimelineEnded);
  onTimelineEndedRef.current = onTimelineEnded;

  const storeRef = useRef<TimelineStore | null>(null);
  if (storeRef.current === null) {
    const initialTime = readFiniteDuration(initialState?.currentTimeSeconds);
    const initialRate = initialState?.playbackRate;
    storeRef.current = createTimelineStore({
      ...DEFAULT_SNAPSHOT,
      currentTimeSeconds: initialTime ?? 0,
      playbackRate:
        initialRate !== undefined && Number.isFinite(initialRate) && initialRate > 0
          ? initialRate
          : 1,
    });
    startedTimeRef.current = initialTime ?? 0;
  }
  const store = storeRef.current;
  const frameClockRef = useRef<PlaybackFrameClockStore | null>(null);
  if (frameClockRef.current === null) {
    frameClockRef.current = createPlaybackFrameClock(store.getSnapshot().currentTimeSeconds);
  }
  const frameClock = frameClockRef.current;

  const activeChannelIds = useMemo(
    () => new Set(channels.map((channel) => channel.id)),
    [channels],
  );
  const activeChannelIdsRef = useRef(activeChannelIds);
  activeChannelIdsRef.current = activeChannelIds;

  const channelDurationMap = useMemo(() => {
    const next = new Map<string, number>();
    for (const channel of channels) {
      const declaredDuration = readFiniteDuration(channel.durationSeconds);
      const metadataDuration = readFiniteDuration(metadataDurations[channel.id]);
      const duration = declaredDuration ?? metadataDuration;
      if (duration !== null) {
        next.set(channel.id, duration);
      }
    }
    return next;
  }, [channels, metadataDurations]);

  const timelineDuration = useMemo(
    () => computeTimelineDuration(channelDurationMap.values(), durationOverrideSeconds),
    [channelDurationMap, durationOverrideSeconds],
  );

  const channelDurationMapRef = useRef(channelDurationMap);
  const timeResolvers = useMemo(
    () => new Map(channels.map((channel) => [channel.id, channel.resolveTime])),
    [channels],
  );
  const timeResolversRef = useRef(timeResolvers);
  useLayoutEffect(() => {
    timeResolversRef.current = timeResolvers;
  }, [timeResolvers]);
  const timelineDurationRef = useRef(timelineDuration);
  const driftToleranceRef = useRef(driftToleranceSeconds);
  const endEpsilonRef = useRef(endEpsilonSeconds);
  const channelEndBehaviorRef = useRef(channelEndBehavior);
  const emitIntervalRef = useRef(emitIntervalMs ?? 0);
  const waitForBufferRef = useRef(waitForBuffer);
  const bufferAheadRef = useRef(DEFAULT_BUFFER_AHEAD_SECONDS);

  channelDurationMapRef.current = channelDurationMap;
  timelineDurationRef.current = timelineDuration;
  driftToleranceRef.current = driftToleranceSeconds;
  endEpsilonRef.current = endEpsilonSeconds;
  channelEndBehaviorRef.current = channelEndBehavior;
  emitIntervalRef.current = emitIntervalMs ?? 0;
  waitForBufferRef.current = waitForBuffer;
  bufferAheadRef.current =
    Number.isFinite(bufferAheadSeconds) && bufferAheadSeconds >= 0
      ? bufferAheadSeconds
      : DEFAULT_BUFFER_AHEAD_SECONDS;

  const cancelFrame = useCallback(() => {
    if (frameRef.current !== null && typeof cancelAnimationFrame === "function") {
      cancelAnimationFrame(frameRef.current);
    }
    frameRef.current = null;
  }, []);

  // 스냅샷 변경의 **유일 통로**. 모든 patch를 여기로 보내면 status가 항상 같은 emit에 일관되게
  // 파생된다(멀티채널은 가상 클럭 기준 — error > ended > buffering > idle > playing/paused, 공유
  // deriveStatus). 빈 patch(`commit({})`)는 status만 재계산. 산재된 refreshStatus 호출을 없애는 래퍼.
  const commit = useCallback(
    (patch: Partial<VideoTimelineState>) => {
      const playing = patch.playing ?? store.getSnapshot().playing;
      store.setSnapshot({
        ...patch,
        status: deriveStatus({
          hasError: errorRef.current,
          ended: endedRef.current,
          ready: timelineDurationRef.current > 0,
          playing,
          // 버퍼 대기 중이거나, 버퍼 대기를 켠 채 길이 로드를 기다리는 재생 의도가 있다.
          buffering:
            holdSecondsRef.current !== null || (waitForBufferRef.current && pendingPlayRef.current),
        }),
      });
      // A time patch lands on the frame clock in the same call. A patch without one leaves the
      // clock alone: between throttled emits it runs ahead of the snapshot, and writing the
      // snapshot's time back would step it backwards.
      if (patch.currentTimeSeconds !== undefined) {
        frameClock.set(store.getSnapshot().currentTimeSeconds);
      }
    },
    [frameClock, store],
  );

  // 늦은 채널 집합을 바꾸고, 달라졌을 때만 구독자를 깨운다(타일은 자기 채널만 selector로 본다).
  const setBufferingChannels = useCallback(
    (next: ReadonlySet<string>) => {
      if (sameChannelSet(bufferingChannelsRef.current, next)) return;
      bufferingChannelsRef.current = next;
      store.notify();
    },
    [store],
  );

  // 모든 seek·대기·blank 경로가 같은 대응을 읽는다. 원본 표본 정책은 호출자가 소유한다.
  const resolveVideoFrame = useCallback((channelId: string, seconds: number) => {
    const resolveTime = timeResolversRef.current.get(channelId);
    const target = resolveTime?.(seconds);
    if (resolveTime && (!target || !Number.isFinite(target.seconds) || target.seconds < 0)) {
      return null;
    }
    return {
      ...resolveChannelFrame(
        target?.seconds ?? seconds,
        channelDurationMapRef.current.get(channelId),
        endEpsilonRef.current,
      ),
      playback: target?.playback ?? "native",
      mapped: resolveTime !== undefined,
    };
  }, []);

  const syncVideos = useCallback(
    (
      targetSeconds: number,
      shouldPlay: boolean,
      options: { forceSeek?: boolean; seekToleranceSeconds?: number } = {},
    ) => {
      const playbackRate = store.getSnapshot().playbackRate;
      const { forceSeek = false, seekToleranceSeconds = driftToleranceRef.current } = options;

      videoAttachments.forEachActiveVideo((channelId, video) => {
        const frame = resolveVideoFrame(channelId, targetSeconds);
        if (frame === null) {
          if (!video.paused) pauseVideo(video);
          return;
        }
        const { hasEnded: channelHasEnded, seekTime: channelTime } = frame;

        if (
          Number.isFinite(playbackRate) &&
          playbackRate > 0 &&
          video.playbackRate !== playbackRate
        ) {
          video.playbackRate = playbackRate;
        }

        const tolerance = frame.playback === "frame" ? 0 : seekToleranceSeconds;
        if (forceSeek || Math.abs(video.currentTime - channelTime) > tolerance) {
          seekVideo(video, channelTime);
        }

        // 엘리먼트가 이미 원하는 상태가 아닐 때만 play/pause를 건드린다 — 매 rAF tick마다
        // play()/pause()를 부르면 다채널에서 미디어 promise가 들끓는다. 네트워크를 기다리는
        // video는 `paused === false`인 채 멈춰 있어 이 가드에 걸리지 않는다 — 그 대기는
        // `waitForBuffer`가 readyState로 따로 판정한다.
        const wantPlay = shouldPlay && !channelHasEnded && frame.playback === "native";
        if (wantPlay) {
          if (video.paused) playVideo(video);
        } else if (!video.paused) {
          pauseVideo(video);
        }
      });
    },
    [resolveVideoFrame, store, videoAttachments],
  );

  // virtual clock 기준점(anchor)으로 계산한 현재 위치(텔레메트리(telemetry) emit이
  // 스로틀(throttle)돼도 정확하다). clock이 멈춰 있으면 마지막 스냅샷(snapshot) 값으로 fallback한다.
  const currentVirtualSeconds = useCallback(() => {
    if (holdSecondsRef.current !== null) return holdSecondsRef.current;
    const snapshot = store.getSnapshot();
    const duration = timelineDurationRef.current;
    if (frameRef.current === null || duration <= 0) return snapshot.currentTimeSeconds;
    const { nextSeconds } = advanceVirtualClock({
      now: performance.now(),
      startedAt: startedAtRef.current,
      startedTime: startedTimeRef.current,
      playbackRate: snapshot.playbackRate,
      duration,
      endEpsilon: endEpsilonRef.current,
    });
    return nextSeconds;
  }, [store]);

  // 버퍼 대기 판정 대상 채널을 돈다. 자기 끝을 지난 채널, 오류·소스 없는 채널은 기다리지 않는다.
  const forEachAwaitedVideo = useCallback(
    (
      targetSeconds: number,
      callback: (
        channelId: string,
        video: HTMLVideoElement,
        channelSeconds: number,
        channelEndSeconds: number,
        frame: NonNullable<ReturnType<typeof resolveVideoFrame>>,
      ) => void,
    ) => {
      const durations = channelDurationMapRef.current;
      const endEpsilon = endEpsilonRef.current;
      const timelineDuration = timelineDurationRef.current;
      videoAttachments.forEachActiveVideo((channelId, video) => {
        const channelDuration = durations.get(channelId);
        const frame = resolveVideoFrame(channelId, targetSeconds);
        if (frame === null || frame.hasEnded || isMediaUnavailable(video)) return;
        const channelEndSeconds = Math.max(0, (channelDuration ?? timelineDuration) - endEpsilon);
        callback(channelId, video, frame.seekTime, channelEndSeconds, frame);
      });
    },
    [resolveVideoFrame, videoAttachments],
  );

  // 대기 위치에서 아직 출발 조건을 못 채운 채널.
  const collectUnreadyChannels = useCallback(
    (heldSeconds: number): ReadonlySet<string> => {
      const unready = new Set<string>();
      forEachAwaitedVideo(
        heldSeconds,
        (channelId, video, channelSeconds, channelEndSeconds, frame) => {
          const ready =
            frame.playback === "frame"
              ? video.readyState >= 2 && !video.seeking
              : isMediaReadyToPlay(video, {
                  seconds: channelSeconds,
                  aheadSeconds: bufferAheadRef.current,
                  endSeconds: channelEndSeconds,
                });
          if (!ready) unready.add(channelId);
        },
      );
      return unready.size > 0 ? unready : NO_CHANNELS;
    },
    [forEachAwaitedVideo],
  );

  // 재생 중 다음 프레임을 그릴 수 없는 채널이 있으면 그중 가장 늦은 위치를 돌려준다. 되돌림 폭은
  // drift 허용치 이하다 — 그보다 뒤처진 채널은 대기 위치로 seek된다. 없으면 null.
  const findStarvedHoldSeconds = useCallback(
    (clockSeconds: number): number | null => {
      const positions: number[] = [];
      forEachAwaitedVideo(clockSeconds, (_channelId, video, _seconds, _end, frame) => {
        const starved =
          frame.playback === "frame"
            ? video.readyState < 2 || video.seeking
            : isMediaStarved(video);
        if (!starved) return;
        // 원본 mp4 시각은 공통 시각이 아니다. 역변환을 추측해 시계를 되감지 않는다.
        positions.push(
          !frame.mapped && Number.isFinite(video.currentTime) ? video.currentTime : clockSeconds,
        );
      });
      if (positions.length === 0) return null;
      return clamp(
        Math.min(...positions),
        Math.max(0, clockSeconds - driftToleranceRef.current),
        clockSeconds,
      );
    },
    [forEachAwaitedVideo],
  );

  // 가상 시계를 heldSeconds에 세우고 전 채널을 그 위치에 멈춘다. 이미 그 자리인 채널은 seek하지
  // 않아, 데이터를 기다리던 채널이 받던 요청을 그대로 이어 간다. 명시적 seek(`forceSeek`)만
  // 전 채널을 정확히 옮긴다.
  const holdAt = useCallback(
    (heldSeconds: number, { forceSeek = false }: { forceSeek?: boolean } = {}) => {
      holdSecondsRef.current = heldSeconds;
      endedRef.current = false;
      syncVideos(
        heldSeconds,
        false,
        forceSeek ? { forceSeek: true } : { seekToleranceSeconds: HOLD_ALIGN_TOLERANCE_SECONDS },
      );
      setBufferingChannels(collectUnreadyChannels(heldSeconds));
      commit({
        playing: true,
        currentTimeSeconds: heldSeconds,
        durationSeconds: timelineDurationRef.current,
      });
    },
    [collectUnreadyChannels, commit, setBufferingChannels, syncVideos],
  );

  // 대기를 풀고 대기 위치에서 시계를 다시 출발시킨다.
  const releaseHold = useCallback(
    (now: number) => {
      const heldSeconds = holdSecondsRef.current;
      if (heldSeconds === null) return;
      holdSecondsRef.current = null;
      setBufferingChannels(NO_CHANNELS);
      startedAtRef.current = now;
      startedTimeRef.current = heldSeconds;
      syncVideos(heldSeconds, true, { seekToleranceSeconds: HOLD_ALIGN_TOLERANCE_SECONDS });
      commit({
        playing: true,
        currentTimeSeconds: heldSeconds,
        durationSeconds: timelineDurationRef.current,
      });
    },
    [commit, setBufferingChannels, syncVideos],
  );

  // 재생 의도가 사라지면(pause·reset·길이 소실) 출발을 기다릴 이유도 없다.
  const clearHold = useCallback(() => {
    holdSecondsRef.current = null;
    setBufferingChannels(NO_CHANNELS);
  }, [setBufferingChannels]);

  const startRaf = useCallback(() => {
    if (typeof requestAnimationFrame !== "function") return;
    cancelFrame();
    let lastEmitAt = Number.NEGATIVE_INFINITY;

    const tick = (now: number) => {
      const duration = timelineDurationRef.current;
      if (duration <= 0) {
        // 재생 중 길이가 사라진 경우(채널 제거 등)의 안전 정지. play()는 길이 로드 전엔 rAF를
        // 시작하지 않으므로(아래 pending 경로), autoplay-on-ready는 여기로 오지 않는다.
        frameRef.current = null;
        clearHold();
        commit({ playing: false, currentTimeSeconds: 0, durationSeconds: 0 });
        return;
      }

      // 버퍼 대기 중: 시계는 멈춰 있고 출발 조건만 본다. 대기 진입·해제는 스로틀과 무관하게
      // 바로 emit한다 — status가 바뀌는 순간이다.
      const heldSeconds = holdSecondsRef.current;
      if (heldSeconds !== null) {
        const unready = waitForBufferRef.current
          ? collectUnreadyChannels(heldSeconds)
          : NO_CHANNELS;
        if (unready.size > 0) {
          setBufferingChannels(unready);
        } else {
          releaseHold(now);
          lastEmitAt = now;
        }
        frameRef.current = requestAnimationFrame(tick);
        return;
      }

      const { nextSeconds, reachedEnd } = advanceVirtualClock({
        now,
        startedAt: startedAtRef.current,
        startedTime: startedTimeRef.current,
        playbackRate: store.getSnapshot().playbackRate,
        duration,
        endEpsilon: endEpsilonRef.current,
      });

      // 채널 하나라도 다음 프레임을 그릴 수 없으면 시계를 세운다. 그대로 흘려보내면 drift 보정이
      // 아직 받지 못한 위치로 seek를 반복해 받던 요청을 끊는다.
      if (waitForBufferRef.current && !reachedEnd) {
        const holdSeconds = findStarvedHoldSeconds(nextSeconds);
        if (holdSeconds !== null) {
          holdAt(holdSeconds);
          lastEmitAt = now;
          frameRef.current = requestAnimationFrame(tick);
          return;
        }
      }

      syncVideos(nextSeconds, !reachedEnd);
      // 끝 도달 플래그는 emit 전에 세워, 같은 emit의 status가 곧바로 ended가 되게 한다.
      if (reachedEnd) endedRef.current = true;
      // 프레임 시계는 스로틀과 무관하게 매 tick 진행한다 — 커서는 이걸, React 상태는 스냅샷을 읽는다.
      frameClock.set(reachedEnd ? duration : nextSeconds);

      // 끝에 도달했거나 스로틀(throttle) 간격이 지났을 때만 텔레메트리(telemetry)를 emit한다.
      const interval = emitIntervalRef.current;
      if (reachedEnd || interval <= 0 || now - lastEmitAt >= interval) {
        lastEmitAt = now;
        commit({
          playing: !reachedEnd,
          // 끝 도달 시 playhead를 정확히 duration으로 스냅한다 — reachedEnd는 [duration-epsilon,
          // duration] 구간에서 true라, 안 그러면 ended인데 currentTime<duration이라 소비자 파생
          // played<1이 된다(SWD-418).
          currentTimeSeconds: reachedEnd ? duration : nextSeconds,
          durationSeconds: duration,
        });
      }

      if (!reachedEnd) {
        frameRef.current = requestAnimationFrame(tick);
      } else {
        frameRef.current = null;
        onTimelineEndedRef.current?.(store.getSnapshot());
      }
    };

    frameRef.current = requestAnimationFrame(tick);
  }, [
    cancelFrame,
    clearHold,
    collectUnreadyChannels,
    commit,
    findStarvedHoldSeconds,
    frameClock,
    holdAt,
    releaseHold,
    setBufferingChannels,
    store,
    syncVideos,
  ]);

  // 반환 ref callback 하나가 곧 attachment identity다. channelId만 다시 조회해 cleanup하면
  // replacement가 먼저 등록된 뒤 도착한 stale null이 최신 element를 해제할 수 있으므로,
  // controller가 attachment/channel/element 수명주기를 구분해 원자적으로 전환한다.
  const registerVideo = useCallback(
    (channelId: string) => {
      const attachment = videoAttachments.createAttachment(channelId);

      return (element: HTMLVideoElement | null) => {
        if (!element) {
          videoAttachments.detachFromRef(attachment);
          return;
        }

        const updateMetadataDuration = () => {
          if (!videoAttachments.isActive(attachment, element)) return;
          const duration = readVideoDuration(element);
          if (duration === null) return;

          setMetadataDurations((prev) =>
            prev[channelId] === duration ? prev : { ...prev, [channelId]: duration },
          );
          const frame = resolveVideoFrame(channelId, currentVirtualSeconds());
          if (frame !== null) seekVideo(element, frame.seekTime);
        };

        const attached = videoAttachments.attach(attachment, element, {
          channelIsActive: activeChannelIdsRef.current.has(channelId),
          subscribe: (isCurrent) =>
            subscribeVideoEvents(element, {
              loadedmetadata: updateMetadataDuration,
              error: () => {
                if (!isCurrent()) return;
                errorRef.current = true;
                commit({});
              },
            }),
        });
        if (!attached) return;

        updateMetadataDuration();
        const snapshot = store.getSnapshot();
        // 버퍼 대기 중에 붙은 채널은 멈춘 채 위치만 맞춘다 — 출발은 다음 tick의 판정이 정한다.
        syncVideos(
          currentVirtualSeconds(),
          snapshot.playing && timelineDurationRef.current > 0 && holdSecondsRef.current === null,
          { forceSeek: true },
        );
      };
    },
    [commit, currentVirtualSeconds, resolveVideoFrame, store, syncVideos, videoAttachments],
  );

  useLayoutEffect(() => {
    syncVideos(
      currentVirtualSeconds(),
      store.getSnapshot().playing && holdSecondsRef.current === null,
    );
    store.notify();
  }, [timeResolvers, currentVirtualSeconds, store, syncVideos]);

  // 채널 목록이 바뀌면 사라진 채널의 video를 정리하고 메타데이터 길이도 솎아낸다.
  useEffect(() => {
    videoAttachments.removeInactiveChannels(activeChannelIds);

    setMetadataDurations((prev) => {
      const next: Record<string, number> = {};
      let changed = false;
      for (const [channelId, duration] of Object.entries(prev)) {
        if (activeChannelIds.has(channelId)) {
          next[channelId] = duration;
        } else {
          changed = true;
        }
      }
      return changed ? next : prev;
    });

    // 채널 세트가 바뀌면(새 콘텐츠) 누적 error 플래그를 초기화하고 status를 재평가한다.
    // (endedRef는 여기서 다루지 않는다 — 채널 간 변경은 클럭(seek time) 이동과는 별개이고,
    //  endedRef는 가상 클럭이 끝에 닿았는지의 타임라인 상태라 클럭을 움직이는 play/seek/reset에서만
    //  초기화한다.)
    errorRef.current = false;
    commit({});
  }, [activeChannelIds, commit, videoAttachments]);

  // 타임라인 길이가 바뀌면 스냅샷을 정규화(reconcile)한다.
  useEffect(() => {
    // autoplay-on-ready: 길이 로드를 기다리는 보류된 재생 의도가 있고 아직 길이가 없으면,
    // 정규화 리셋이 낙관적 playing:true를 되돌리지 않도록 건너뛴다. (pending 동안은 rAF가 없다.)
    if (pendingPlayRef.current && timelineDuration <= 0) {
      store.notify();
      return;
    }

    const patch = reconcileDurationPatch(store.getSnapshot(), timelineDuration);
    if (patch && timelineDuration <= 0) {
      // 예약된 tick을 취소하므로 tick의 길이 소실 분기가 하는 대기 해제도 여기서 한다.
      cancelFrame();
      clearHold();
    }
    commit(patch ?? {});

    // 길이가 확보된 바로 그 순간(메타데이터 로드 = 0→>0 전이)에 보류된 재생을 재개한다.
    if (pendingPlayRef.current && timelineDuration > 0) {
      pendingPlayRef.current = false;
      playRef.current();
    }

    // `isChannelOutOfTime`에 쓰이는 채널별 길이는 공유 타임라인 길이가 그대로여도 바뀔 수
    // 있다(더 짧은 채널의 메타데이터가 늦게 로드되는 경우) — 구독자를 깨워 blank 타일이 다시
    // 판정하게 한다. 선택값이 안 바뀐 셀렉터(selector)는 `useVideoTimeline` 캐시가 걸러 no-op.
    store.notify();
  }, [timelineDuration, channelDurationMap, store, cancelFrame, clearHold, commit]);

  // On unmount, cancel rAF, detach listeners, and release all video media resources.
  useEffect(
    () => () => {
      cancelFrame();
      videoAttachments.dispose();
    },
    [cancelFrame, videoAttachments],
  );

  const play = useCallback(() => {
    const duration = timelineDurationRef.current;
    if (duration <= 0) {
      // autoplay-on-ready: 길이가 아직 없으면(메타데이터 미로드) 재생 의도만 기록하고 낙관적으로
      // playing:true를 커밋한다(바가 pause 버튼 표시). 길이가 들어오는 즉시 정규화 effect가 재개한다.
      // status는 not-ready라 "idle"이고, 버퍼 대기를 켰으면 "buffering"이다.
      pendingPlayRef.current = true;
      commit({ playing: true });
      return;
    }
    pendingPlayRef.current = false;
    const snapshot = store.getSnapshot();
    const startSeconds = computePlayStart(
      snapshot.currentTimeSeconds,
      duration,
      endEpsilonRef.current,
    );
    startedAtRef.current = performance.now();
    startedTimeRef.current = startSeconds;
    endedRef.current = false;
    if (waitForBufferRef.current) {
      // 버퍼 대기: 시작 위치에 멈춘 채 출발 조건을 채운다. 이미 받아 두었으면 바로 출발한다.
      holdAt(startSeconds);
      if (bufferingChannelsRef.current.size === 0) releaseHold(performance.now());
      startRaf();
      return;
    }
    syncVideos(startSeconds, true, { forceSeek: true });
    commit({
      playing: true,
      currentTimeSeconds: startSeconds,
      durationSeconds: duration,
    });
    startRaf();
  }, [commit, holdAt, releaseHold, startRaf, store, syncVideos]);
  playRef.current = play;

  const pause = useCallback(() => {
    pendingPlayRef.current = false;
    const seconds = currentVirtualSeconds();
    const wasHolding = holdSecondsRef.current !== null;
    clearHold();
    cancelFrame();
    // 버퍼 대기 중이었다면 채널은 이미 그 위치에 멈춰 있다 — 같은 위치로 다시 seek하지 않는다.
    syncVideos(
      seconds,
      false,
      wasHolding ? { seekToleranceSeconds: HOLD_ALIGN_TOLERANCE_SECONDS } : { forceSeek: true },
    );
    commit({ playing: false, currentTimeSeconds: seconds });
  }, [cancelFrame, clearHold, commit, currentVirtualSeconds, syncVideos]);

  const toggle = useCallback(() => {
    if (store.getSnapshot().playing) pause();
    else play();
  }, [pause, play, store]);

  const seekToSeconds = useCallback(
    (seconds: number) => {
      const duration = timelineDurationRef.current;
      const next = boundToTimeline(seconds, duration);
      const snapshot = store.getSnapshot();
      startedAtRef.current = performance.now();
      startedTimeRef.current = next;
      endedRef.current = false;
      if (waitForBufferRef.current && snapshot.playing && duration > 0) {
        // 버퍼 대기: 재생 중 seek는 새 위치에 멈춘 채 재생 시작과 같은 출발 조건을 채운다.
        holdAt(next, { forceSeek: true });
        if (frameRef.current === null) startRaf();
        return;
      }
      // 재생 중인 clock은 매 tick마다 위 기준점(anchor)을 읽으므로 rAF를 재시작할 필요가 없다.
      // 단 길이 미확보(pending autoplay-on-ready) 중엔 미디어를 재생시키지 않는다 — 낙관적
      // playing:true를 그대로 넘기면 metadata 전 video.play()가 불려 defer-until-ready 계약을
      // 깬다. 위치·의도는 보존하고, 실제 재생은 길이 확보 시 재개 경로가 시작한다.
      syncVideos(next, snapshot.playing && duration > 0, { forceSeek: true });
      commit({ currentTimeSeconds: next, durationSeconds: duration });
    },
    [commit, holdAt, startRaf, store, syncVideos],
  );

  const setPlaybackRate = useCallback(
    (rate: number) => {
      if (!Number.isFinite(rate) || rate <= 0) return;
      const seconds = currentVirtualSeconds();
      // 새 속도가 과거가 아니라 지금부터 적용되도록 기준점(anchor)을 다시 잡는다.
      startedAtRef.current = performance.now();
      startedTimeRef.current = seconds;
      videoAttachments.forEachActiveVideo((_channelId, video) => {
        video.playbackRate = rate;
      });
      commit({ playbackRate: rate, currentTimeSeconds: seconds });
    },
    [commit, currentVirtualSeconds, videoAttachments],
  );

  const reset = useCallback(() => {
    pendingPlayRef.current = false;
    clearHold();
    cancelFrame();
    startedAtRef.current = performance.now();
    startedTimeRef.current = 0;
    endedRef.current = false;
    errorRef.current = false;
    syncVideos(0, false, { forceSeek: true });
    commit({
      playing: false,
      currentTimeSeconds: 0,
      durationSeconds: timelineDurationRef.current,
    });
  }, [cancelFrame, clearHold, commit, syncVideos]);

  const getChannelDuration = useCallback(
    (channelId: string) => channelDurationMapRef.current.get(channelId) ?? null,
    [],
  );

  // blank 동작일 때, 해당 채널이 자기 길이를 지나 끝났는지 판정한다(blank 오버레이용).
  const isChannelOutOfTime = useCallback(
    (channelId: string) => {
      const frame = resolveVideoFrame(channelId, store.getSnapshot().currentTimeSeconds);
      if (frame === null) return true;
      if (channelEndBehaviorRef.current !== "blank") return false;
      if (frame.mapped) return frame.hasEnded;
      return isChannelPastEnd(
        store.getSnapshot().currentTimeSeconds,
        channelDurationMapRef.current.get(channelId),
        endEpsilonRef.current,
      );
    },
    [resolveVideoFrame, store],
  );

  const isChannelBuffering = useCallback(
    (channelId: string) =>
      holdSecondsRef.current !== null && bufferingChannelsRef.current.has(channelId),
    [],
  );

  return useMemo<SyncedVideoTimelineApi>(
    () => ({
      subscribe: store.subscribe,
      getSnapshot: store.getSnapshot,
      frameClock,
      registerVideo,
      getChannelDuration,
      isChannelOutOfTime,
      isChannelBuffering,
      play,
      pause,
      toggle,
      seekToSeconds,
      setPlaybackRate,
      reset,
    }),
    [
      store,
      frameClock,
      registerVideo,
      getChannelDuration,
      isChannelOutOfTime,
      isChannelBuffering,
      play,
      pause,
      toggle,
      seekToSeconds,
      setPlaybackRate,
      reset,
    ],
  );
}
